/**
 * Policy re-acceptance gate (§15).
 *
 * Shown in place of any protected page when a signed-in account's stored terms or
 * privacy version is below the current one (`src/config/policies.js`). It offers the
 * updated documents and a single accept action; declining is signing out. Dormant
 * until a policy version is bumped.
 */

import { el, icon } from "../ui/dom.js";
import { errorAlert, pageHeader } from "../ui/patterns.js";
import { POLICY_VERSION } from "../config/policies.js";

/**
 * @param {import("../app/router.js").RouteContext} context
 * @returns {Node}
 */
export function renderAcceptPolicies(context) {
  const frag = document.createDocumentFragment();
  frag.append(
    pageHeader(
      "Please review our policies",
      "We have updated our terms of use and privacy policy. Please review and accept them to continue.",
    ),
  );

  const feedback = el("div", { "aria-live": "assertive" });

  frag.append(
    el("ul", { class: "kin-plain-list" }, [
      el("li", {}, [el("a", { href: "/terms", dataset: { link: "" } }, ["Read the terms of use"])]),
      el("li", {}, [
        el("a", { href: "/privacy", dataset: { link: "" } }, ["Read the privacy policy"]),
      ]),
    ]),
  );

  const accept = el(
    "wa-button",
    {
      variant: "brand",
      appearance: "filled",
      on: {
        click: async () => {
          feedback.replaceChildren();
          accept.setAttribute("loading", "");
          try {
            await context.services.account.acceptPolicies({
              termsVersion: POLICY_VERSION.terms,
              privacyVersion: POLICY_VERSION.privacy,
            });
            // Re-read the account; the router clears the gate on the next render.
            await context.services.auth.refresh();
          } catch (error) {
            accept.removeAttribute("loading");
            feedback.replaceChildren(
              errorAlert(
                error instanceof Error
                  ? error.message
                  : "Could not save your acceptance. Try again.",
              ),
            );
          }
        },
      },
    },
    [icon("circle-check"), "Accept and continue"],
  );

  const signOut = el(
    "wa-button",
    {
      appearance: "outlined",
      on: {
        click: async () => {
          await context.services.auth.signOut();
          context.navigate("/");
        },
      },
    },
    [icon("right-to-bracket"), "Sign out"],
  );

  frag.append(el("div", { class: "kin-actions" }, [accept, signOut]), feedback);
  return frag;
}
